import {AxiosError} from "axios";
import {createContext, useCallback, useEffect, useMemo, useState} from 'react'
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {AdminDetails, AuthApi} from "../api/admin/generated";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {Assert} from "../utils/Assert";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";

const authApi = new AuthApi(ApiConfig.config())

export interface IAuthenticatedAdminContext {
    authenticatedAdmin: AdminDetails | undefined,
    clearAuthenticatedAdmin: () => void,
}

export const AuthenticatedAdminContext = createContext<IAuthenticatedAdminContext>({
    authenticatedAdmin: undefined,
    clearAuthenticatedAdmin: () => {},
});

interface AuthenticatedAdminContextProviderProps {
    children: React.ReactNode;
}

/**
 * Provides the current authenticated admin context to its children components.
 */
export function AuthenticatedAdminContextProvider({children} : Readonly<AuthenticatedAdminContextProviderProps>)
{
    const [authenticatedAdmin, setAuthenticatedAdmin] = useState<AdminDetails | undefined>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * Navigate is used to navigate to the login page if the admin is no longer authenticated
     */
    const navigate = useNavigate();

    function clearAuthenticatedAdmin() {
        setAuthenticatedAdmin(undefined);
    }

    const getAuthenticatedAdmin = useCallback(() =>
    {
        logger.debug("Checking if admin is authenticated")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        authApi.authControllerGetAuthenticatedAdmin().then(response =>
        {
            logger.info(`Admin ${response.data.name} was authenticated`);

            setAuthenticatedAdmin(Assert.assertNotNullOrUndefined(response.data));
        }).catch((err: Error | AxiosError) => {
            if (err instanceof AxiosError)
            {
                // do not log 401 errors
                if (err.response?.status !== 401) {
                    logger.error("Error getting authenticated admin", err);
                }
                else {
                    logger.info("Admin is not authenticated")
                }
            }

            clearAuthenticatedAdmin();

            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
        }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }, [setSpinnerStateCounter, navigate])

    useEffect(() => {
        getAuthenticatedAdmin();
    }, [getAuthenticatedAdmin]);

    const exposedData = useMemo<IAuthenticatedAdminContext>(() => {
        const authenticatedAdminContext : IAuthenticatedAdminContext =
        {
            authenticatedAdmin : authenticatedAdmin,
            clearAuthenticatedAdmin : clearAuthenticatedAdmin
        }

        return authenticatedAdminContext;
    }, [authenticatedAdmin]);

    return(
        <AuthenticatedAdminContext.Provider value = {exposedData}>
            {children}
        </AuthenticatedAdminContext.Provider>
    )
}